import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { AzioneComando, ImpiantoSintesi, Lettura, RispostaComando } from '@emacomfort/domain';
import { CloudParco, SimulatorParco, impiantoParametrico, type DeviceClient } from '@emacomfort/device-client';
import { creaDeviceStore, selezionaComando, selezionaObsoleto, type DeviceStore, type StatoComando, type StatoDispositivo } from './store';
import { useMarchio } from './BrandProvider';
import { usePreferenze } from './preferenze';
import { useSimulatore } from './simulatore';
import { apiBaseUrl, richiedeLoginQui, tokenCorrente } from './auth';

/** Ciò di cui le pagine hanno bisogno dal parco: la lista impianti. Il tipo è
 *  ridotto a questo perché sia il simulatore sia il cloud lo soddisfino. */
export interface ParcoVista {
  listaImpianti(): Promise<ImpiantoSintesi[]>;
}

interface ContestoDispositivo {
  client: DeviceClient;
  store: DeviceStore;
  parco: ParcoVista;
  impiantoId: string;
  selezionaImpianto(id: string): void;
  invia(azione: AzioneComando): Promise<RispostaComando>;
}

const Contesto = createContext<ContestoDispositivo | null>(null);

/**
 * Lo strato `device/` della specifica (§9.5): le feature parlano con
 * `DeviceClient`, mai con la rete. Il client è il `CloudClient` — che parla con
 * l'API — su un host installatore e sulla macchina di uno sviluppatore con la
 * build configurata; è il simulatore sugli host vetrina, sulla build demo, e
 * ovunque il simulatore sia stato chiesto esplicitamente da `/simulatore`.
 * È lo stesso punto di innesto promesso da sempre.
 */
export function DeviceProvider({ children }: { children: ReactNode }) {
  return richiedeLoginQui() ? (
    <CloudDeviceProvider>{children}</CloudDeviceProvider>
  ) : (
    <SimulatorDeviceProvider>{children}</SimulatorDeviceProvider>
  );
}

// ── Simulatore ────────────────────────────────────────────────────────────

function SimulatorDeviceProvider({ children }: { children: ReactNode }) {
  const { marchio } = useMarchio();
  const impiantoSalvato = usePreferenze((s) => s.impiantoPerMarchio[marchio.id]);
  const setImpiantoPref = usePreferenze((s) => s.setImpianto);
  const configurato = useSimulatore((s) => s.attivo);
  const configurazione = useSimulatore((s) => s.configurazione);

  // Una configurazione scelta in `/simulatore` sostituisce il parco del
  // marchio con l'impianto che descrive: è quella la cosa da provare, e
  // affiancarla alle fixture la nasconderebbe dietro un menu.
  const parco = useMemo(
    () =>
      configurato
        ? new SimulatorParco('simulatore', [{ impianto: impiantoParametrico(configurazione) }])
        : new SimulatorParco(marchio.tenantId, marchio.parco()),
    [marchio, configurato, configurazione],
  );
  useEffect(() => () => parco.ferma(), [parco]);

  const [impiantoId, setImpiantoId] = useState<string>(() => {
    try {
      if (impiantoSalvato) {
        parco.clientPer(impiantoSalvato);
        return impiantoSalvato;
      }
    } catch {
      /* impianto non più nel parco */
    }
    return parco.impiantoPredefinitoId;
  });

  const client = useMemo(() => parco.clientPer(impiantoId), [parco, impiantoId]);
  const { store, avvia, invia } = useMemo(() => creaDeviceStore(client), [client]);
  useEffect(() => avvia(), [avvia]);

  const valore = useMemo<ContestoDispositivo>(
    () => ({
      client,
      store,
      parco,
      impiantoId,
      selezionaImpianto: (id) => {
        parco.clientPer(id);
        setImpiantoPref(marchio.id, id);
        setImpiantoId(id);
      },
      invia,
    }),
    [client, store, parco, impiantoId, invia, marchio.id, setImpiantoPref],
  );

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

// ── Cloud (host installatore, autenticato) ────────────────────────────────

function CloudDeviceProvider({ children }: { children: ReactNode }) {
  const { marchio } = useMarchio();
  const impiantoSalvato = usePreferenze((s) => s.impiantoPerMarchio[marchio.id]);
  const setImpiantoPref = usePreferenze((s) => s.setImpianto);

  // We are inside AuthBoundary, so a token exists; coerce the seam's nullable
  // return to satisfy the client's getToken (a '' would simply 401).
  const parco = useMemo(
    () => new CloudParco({ baseUrl: apiBaseUrl(), getToken: async () => (await tokenCorrente()) ?? '' }),
    [],
  );

  // Il parco è remoto: la lista impianti arriva in modo asincrono, quindi il
  // client non esiste finché non sappiamo quale impianto aprire.
  const [flotta, setFlotta] = useState<ImpiantoSintesi[] | null>(null);
  const [impiantoId, setImpiantoId] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    parco
      .listaImpianti()
      .then((lista) => {
        if (!vivo) return;
        setFlotta(lista);
        const salvato = impiantoSalvato && lista.some((i) => i.id === impiantoSalvato) ? impiantoSalvato : null;
        setImpiantoId(salvato ?? lista[0]?.id ?? null);
      })
      .catch(() => vivo && setErrore('flotta'));
    return () => {
      vivo = false;
    };
  }, [parco, impiantoSalvato]);

  if (errore) {
    return <Caricamento messaggio="Impossibile caricare gli impianti." />;
  }
  if (flotta === null || impiantoId === null) {
    return <Caricamento messaggio={flotta && flotta.length === 0 ? 'Nessun impianto assegnato.' : undefined} />;
  }

  return (
    <CloudDispositivo
      parco={parco}
      impiantoIdIniziale={impiantoId}
      onSeleziona={(id) => setImpiantoPref(marchio.id, id)}
    >
      {children}
    </CloudDispositivo>
  );
}

function CloudDispositivo({
  parco,
  impiantoIdIniziale,
  onSeleziona,
  children,
}: {
  parco: CloudParco;
  impiantoIdIniziale: string;
  onSeleziona(id: string): void;
  children: ReactNode;
}) {
  const [impiantoId, setImpiantoId] = useState(impiantoIdIniziale);
  const client = useMemo(() => parco.clientPer(impiantoId), [parco, impiantoId]);
  const { store, avvia, invia } = useMemo(() => creaDeviceStore(client), [client]);
  useEffect(() => avvia(), [avvia]);

  const valore = useMemo<ContestoDispositivo>(
    () => ({
      client,
      store,
      parco,
      impiantoId,
      selezionaImpianto: (id) => {
        onSeleziona(id);
        setImpiantoId(id);
      },
      invia,
    }),
    [client, store, parco, impiantoId, invia, onSeleziona],
  );

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

function Caricamento({ messaggio }: { messaggio?: string }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--pweb-testo-tenue, #888)' }} aria-busy={!messaggio}>
      {messaggio ?? '…'}
    </div>
  );
}

/** Solo per i test: monta il contesto attorno a un client già creato. */
export function DeviceContestoTest({ client, store, invia, children }: { client: DeviceClient; store: DeviceStore; invia: ContestoDispositivo['invia']; children: ReactNode }) {
  const parco = useMemo<ParcoVista>(() => new SimulatorParco('test', []), []);
  const valore = useMemo<ContestoDispositivo>(
    () => ({ client, store, parco, impiantoId: client.impiantoId, selezionaImpianto: () => undefined, invia }),
    [client, store, parco, invia],
  );
  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}

export function useDevice(): ContestoDispositivo {
  const c = useContext(Contesto);
  if (!c) throw new Error('useDevice fuori da DeviceProvider');
  return c;
}

export function useStato<T>(selettore: (s: StatoDispositivo) => T): T {
  const { store } = useDevice();
  return useStore(store, selettore);
}

export function useImpianto() {
  return useStato((s) => s.impianto);
}

export function useObsoleto(): { obsoleto: boolean; ultimaLettura: number | null } {
  const obsoleto = useStato(selezionaObsoleto);
  const ultimaLettura = useStato((s) => s.ultimaLettura);
  return { obsoleto, ultimaLettura };
}

export interface LetturaVista {
  valore: number | null;
  unita: string;
  q: Lettura['q'] | 'assente';
  ts: number | null;
  obsoleto: boolean;
}

export function useLettura(canaleId: string | undefined): LetturaVista {
  const lettura = useStato((s) => (canaleId ? s.letture[canaleId] : undefined));
  const { obsoleto } = useObsoleto();
  if (!lettura) return { valore: null, unita: '', q: 'assente', ts: null, obsoleto: true };
  return { valore: lettura.v, unita: lettura.u, q: lettura.q, ts: lettura.ts, obsoleto };
}

export function useComando(oggetto: string): StatoComando | undefined {
  return useStato(selezionaComando(oggetto));
}
